import { MatterRelatedMatterContextMenu } from "./MatterRelatedMatterContextMenu";
import { MatterSubItemEventLogsContextMenu } from "@/components/features/matter/event-logs/MatterSubItemEventLogsContextMenu";
import { MatterSubItemEventLogsModal } from "@/components/features/matter/event-logs/MatterSubItemEventLogsModal";
import { MatterSubItemFileReferencesContextMenu } from "@/components/features/matter/file-repo/MatterSubItemFileReferencesContextMenu";
import { MatterSubItemFileReferencesModal } from "@/components/features/matter/file-repo/MatterSubItemFileReferencesModal";
import { FileReferences } from "@/components/features/matter/file-repo/FileReferences";
import {
  MatterSubItemFileReferenceUpload,
  type MatterSubItemFileReferenceUploadHandle,
} from "@/components/features/matter/file-repo/MatterSubItemFileReferenceUpload";
import { MatterSubItemNotesContextMenu } from "@/components/features/matter/notes/MatterSubItemNotesContextMenu";
import { MatterSubItemNotesModal } from "@/components/features/matter/notes/MatterSubItemNotesModal";
import { MatterSubItemTasksContextMenu } from "@/components/features/matter/tasks/MatterSubItemTasksContextMenu";
import { MatterSubItemTasksModal } from "@/components/features/matter/tasks/MatterSubItemTasksModal";
import { MatterSubItemGenerateDocumentContextMenu } from "@/components/features/matter/context-menus/MatterSubItemGenerateDocumentContextMenu";
import { ActionConfirmationModal, ContextMenu } from "@/components/ui";
import { useUser } from "@/components/UserProvider";
import { useDeleteMatterIncidentReport } from "@/lib/data/react-query/matters/matter-incident-report";
import { useUnlinkObjectFromMatter } from "@/lib/data/react-query/matters/matter-related-object";
import {
  ACTION_PERMISSION_KEYS,
  ACTION_PERMISSION_VALUES,
} from "@/lib/data/static/permissionOptions";
import type { Matter, MatterIncidentReport } from "@/lib/types";
import { useRef, useState, type ReactNode } from "react";

interface MatterIncidentReportContextMenuProps {
  matter: Matter;
  onOpen: () => void;
  report: MatterIncidentReport;
  trigger: ReactNode | ((onDelete: () => void) => ReactNode);
}

export const MatterIncidentReportContextMenu = ({
  matter,
  onOpen,
  report,
  trigger,
}: MatterIncidentReportContextMenuProps) => {
  const { hasActionPermission } = useUser();
  const [isDeleteConfirmationOpen, setIsDeleteConfirmationOpen] =
    useState(false);
  const [isUnlinkConfirmationOpen, setIsUnlinkConfirmationOpen] =
    useState(false);
  const [isEventLogsOpen, setIsEventLogsOpen] = useState(false);
  const fileReferenceUploadRef =
    useRef<MatterSubItemFileReferenceUploadHandle>(null);
  const [isFileReferencesOpen, setIsFileReferencesOpen] = useState(false);
  const [isCreateFileReferenceOpen, setIsCreateFileReferenceOpen] =
    useState(false);
  const [isNotesOpen, setIsNotesOpen] = useState(false);
  const [isTasksOpen, setIsTasksOpen] = useState(false);
  const deleteIncidentReport = useDeleteMatterIncidentReport({
    successMessage: report.noReport
      ? "No report removed successfully"
      : report.notApplicable
        ? "Not applicable removed successfully"
        : "Incident report deleted successfully",
  });
  const unlinkIncidentReport = useUnlinkObjectFromMatter({
    objectLabel: "Incident report",
  });
  const canDeleteIncidentReport = hasActionPermission(
    ACTION_PERMISSION_KEYS.matterIncidentReport,
    ACTION_PERMISSION_VALUES.delete,
  );
  const reportLabel = report.noReport
    ? "No report"
    : report.notApplicable
      ? "Not applicable"
      : "incident report";
  const copyValue = [
    reportLabel,
    report.status ? `Status: ${report.status}` : undefined,
    report.sentiment ? `Sentiment: ${report.sentiment}` : undefined,
    report.description ? `Description: ${report.description}` : undefined,
  ]
    .filter(Boolean)
    .join("\n");
  const subItem = { id: report.id, type: "incident-report" } as const;

  const handleDelete = () => {
    if (!canDeleteIncidentReport || deleteIncidentReport.isPending) return;
    if (report.noReport || report.notApplicable) {
      deleteIncidentReport.mutate({ matterId: matter.id, id: report.id });
    } else {
      setIsDeleteConfirmationOpen(true);
    }
  };

  if (report.noReport || report.notApplicable) {
    return (
      <>{typeof trigger === "function" ? trigger(handleDelete) : trigger}</>
    );
  }

  return (
    <>
      <ContextMenu
        entityName="incident report"
        open={{ onSelect: onOpen }}
        copy={{
          value: copyValue,
          successLabel: "Incident report details",
        }}
        delete={
          canDeleteIncidentReport
            ? {
                disabled: deleteIncidentReport.isPending,
                onSelect: handleDelete,
              }
            : false
        }
        unlink={
          canDeleteIncidentReport
            ? {
                disabled: unlinkIncidentReport.isPending,
                label: "Unlink incident report",
                onSelect: () => {
                  setIsUnlinkConfirmationOpen(true);
                },
              }
            : false
        }
        trigger={
          typeof trigger === "function" ? trigger(handleDelete) : trigger
        }
      >
        <MatterSubItemGenerateDocumentContextMenu
          matter={matter}
          subItem={subItem}
        />
        <MatterRelatedMatterContextMenu
          matter={matter}
          objectId={report.id}
          objectType="incident-report"
        />
        <MatterSubItemTasksContextMenu
          matter={matter}
          onOpenTasks={() => {
            setIsTasksOpen(true);
          }}
          subItem={subItem}
        />
        <MatterSubItemNotesContextMenu
          matter={matter}
          onOpenNotes={() => {
            setIsNotesOpen(true);
          }}
          subItem={subItem}
        />
        <MatterSubItemFileReferencesContextMenu
          onOpenFileReferences={() => {
            setIsFileReferencesOpen(true);
          }}
          onCreateFileReferences={() => {
            setIsCreateFileReferenceOpen(true);
          }}
          onUploadFileReference={() => {
            fileReferenceUploadRef.current?.openFilePicker();
          }}
        />
        <MatterSubItemEventLogsContextMenu
          onOpenEventLogs={() => {
            setIsEventLogsOpen(true);
          }}
        />
      </ContextMenu>
      <ActionConfirmationModal
        open={isDeleteConfirmationOpen}
        setOpen={(open) => {
          if (!open && !deleteIncidentReport.isPending) {
            setIsDeleteConfirmationOpen(false);
          }
        }}
        title={`Are you sure you want to delete "${reportLabel}"?`}
        description="This permanently deletes the incident report from every matter. This action cannot be undone."
        buttonText={`Delete ${reportLabel}`}
        afterActionFn={async () => {
          await deleteIncidentReport.mutateAsync({
            matterId: matter.id,
            id: report.id,
          });
        }}
        isActionPending={deleteIncidentReport.isPending}
      />
      <ActionConfirmationModal
        open={isUnlinkConfirmationOpen}
        setOpen={(open) => {
          if (!open && !unlinkIncidentReport.isPending) {
            setIsUnlinkConfirmationOpen(false);
          }
        }}
        title={`Unlink "${reportLabel}" from this matter?`}
        description="This removes the incident report from this matter only. If this is its last matter, the incident report will be deleted."
        buttonText={`Unlink ${reportLabel}`}
        afterActionFn={async () => {
          await unlinkIncidentReport.mutateAsync({
            matterId: matter.id,
            objectId: report.id,
            objectType: "incident-report",
          });
        }}
        isActionPending={unlinkIncidentReport.isPending}
      />
      <MatterSubItemTasksModal
        open={isTasksOpen}
        onOpenChange={setIsTasksOpen}
        entityLabel={reportLabel}
        matter={matter}
        subItem={subItem}
      />
      <MatterSubItemNotesModal
        open={isNotesOpen}
        onOpenChange={setIsNotesOpen}
        entityLabel={reportLabel}
        matter={matter}
        subItem={subItem}
      />
      <MatterSubItemFileReferencesModal
        open={isFileReferencesOpen}
        onOpenChange={(open) => {
          setIsFileReferencesOpen(open);
          if (!open) {
            setIsCreateFileReferenceOpen(false);
          }
        }}
        createFileReferenceOpen={
          isFileReferencesOpen ? isCreateFileReferenceOpen : false
        }
        onCreateFileReferenceOpenChange={setIsCreateFileReferenceOpen}
        entityLabel={reportLabel}
        matter={matter}
        subItem={subItem}
      />
      <MatterSubItemEventLogsModal
        open={isEventLogsOpen}
        onOpenChange={setIsEventLogsOpen}
        entityLabel={reportLabel}
        matter={matter}
        subItem={subItem}
      />
      {isCreateFileReferenceOpen && !isFileReferencesOpen ? (
        <FileReferences
          matter={matter}
          subItem={subItem}
          createOnly
          createFileReferenceOpen
          onCreateFileReferenceOpenChange={setIsCreateFileReferenceOpen}
        />
      ) : null}
      <MatterSubItemFileReferenceUpload
        ref={fileReferenceUploadRef}
        matter={matter}
        subItem={subItem}
      />
    </>
  );
};
